import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import saveErrorMessage from '../../../utils/saveErrorMessage';

dayjs.extend(utc);
dayjs.extend(timezone);

// The text of the Google Calendar page, from the design spec sections 9.2 and 9.4.
// The dashboard says "approved", never "confirmed" (R1-L5-008).

export const INTRO =
  'FlowBookings can hide the times you are busy in Google Calendar, and add your approved bookings to a calendar you choose. It sees when you are busy, not what your events are.';

export const CALENDARS_HEADING = 'Check these calendars for conflicts';

export const CALENDARS_NOTE =
  'Busy events in checked calendars hide your booking times, also for classes. If a calendar holds your own schedule or availability blocks, untick it or mark those events as Free in Google.';

export const NOTHING_TICKED = 'No calendar is checked, so Google does not block any time.';

export const DISCONNECT_ONLY_NOTE =
  'Google Calendar is not available for your account right now. You can disconnect to remove your data.';

export const CLAIM_SUCCESS = 'Google Calendar is connected.';

export const WRITE_LOST_NOTE =
  "FlowBookings can no longer add events to this account's calendars. Grant permission again to keep your events up to date.";

// Shown on a `read_only` connection while writes are on (ADR-10, consent states). The spec
// puts this button on the "Add bookings to" card, which comes in a later pull request.
export const GRANT_WRITE_NOTE = 'Grant permission to add bookings to Google.';

export const NEEDS_SHARED_PERMISSION = 'Needs permission to check calendars shared with you';

export const CALENDAR_PROBLEMS = {
  not_found: 'Not found',
  no_access: 'No access',
  too_many_events: 'Too many events',
};

export const DISCONNECT_BODY =
  'FlowBookings stops checking these calendars and stops updating their events. Events already in Google Calendar stay there.';

const SERVER_ERROR = 'Something went wrong. Please try again.';

const TEXT_BY_CODE = {
  calendar_denied: 'You did not give FlowBookings access to your calendars.',
  calendar_no_list: 'FlowBookings needs to see your list of calendars. Please try again and allow it.',
  calendar_list_only:
    'FlowBookings needs permission to check your availability or to add events. Please try again and allow at least one.',
  calendar_claim_mismatch:
    'This connection was started from another FlowBookings account. Nothing was connected.',
  calendar_claim_expired: 'That link has expired. Please connect again.',
  calendar_failed: 'Google did not answer. Please try again in a minute.',
  calendar_unavailable: 'Google Calendar is not available right now. Please try again later.',
  calendar_limit: 'You can connect up to 5 Google accounts.',
  calendar_host_unsupported: 'Google Calendar can be connected only from app.flowbookings.io.',
  calendar_not_found: 'That Google account is no longer connected. Please reload the page.',
  calendar_already_checked: 'This calendar is already checked through another Google account.',
  calendar_owned_elsewhere:
    'This calendar belongs to another Google account you connected. Tick it there.',
  calendar_google_limit:
    'This Google account is connected to too many FlowBookings accounts. Disconnect it from one you no longer use, then try again.',
  calendar_tick_limit: 'You can check up to 10 calendars.',
  calendar_needs_permission:
    'FlowBookings needs permission to check this calendar. Use Grant permission first.',
  calendar_destination_not_owned: 'You can add bookings only to a calendar you own.',
  calendar_writes_off: 'Adding bookings to Google is not available yet.',
  server_error: SERVER_ERROR,
};

// The `#calendar_error=` values of the return from Google (spec 2.2 and 9.4).
const RETURN_CODES = {
  denied: 'calendar_denied',
  no_list: 'calendar_no_list',
  list_only: 'calendar_list_only',
  expired: 'calendar_claim_expired',
  failed: 'calendar_failed',
  unavailable: 'calendar_unavailable',
  google_limit: 'calendar_google_limit',
};

// The text for a refused calendar request. The Axios client rejects with the answer body
// `{ error, messages }`. `validation_error` and unknown codes show the server's messages,
// or the generic text when there are none.
export function errorText(error) {
  const code = error?.error;
  if (TEXT_BY_CODE[code]) return TEXT_BY_CODE[code];
  if (code === 'validation_error') return saveErrorMessage(error);
  return saveErrorMessage(error, SERVER_ERROR);
}

export function returnErrorText(code) {
  return TEXT_BY_CODE[RETURN_CODES[code]] || SERVER_ERROR;
}

export function isCalendarOff(error) {
  return error?.error === 'calendar_off';
}

// An instant as the account owner reads it, in the account zone.
export function formatZoneTime(date, zone) {
  return dayjs(date).tz(zone).format('D MMM YYYY, h:mm A');
}

export function pausedNote(checkedAt, zone) {
  const first = 'Google Calendar is paused while we fix a problem on our side.';
  if (!checkedAt) return first;
  return `${first} Your booking page still uses your calendars as they were at ${formatZoneTime(checkedAt, zone)}.`;
}

export function failingBanner(checkedAt, zone) {
  const first = 'We cannot reach Google Calendar right now.';
  if (!checkedAt) return first;
  return `${first} Your booking page uses your calendar as it was at ${formatZoneTime(checkedAt, zone)}.`;
}

export function brokenBanner(validUntil, zone) {
  const first = 'Google Calendar access was removed or has expired. Please reconnect.';
  if (!validUntil) return first;
  return `${first} After ${formatZoneTime(validUntil, zone)}, times you are busy in Google show as free on your booking page.`;
}
